<!-- B 端 · 本店应付供方预估（§6.1：按供分页 + 只抽屉） -->
<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="warning"
      :closable="false"
      show-icon
      title="本店应付供方预估 · 非结算"
      description="按供应商扫本店应付；点「明细」下钻。汇总应付仅合同结算价。请选择完成时间（默认近 30 天）。货款线下付；单据见「外发结算单」。"
    />

    <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="完成时间" required>
        <el-date-picker
          v-model="queryParams.finishTime"
          type="daterange"
          value-format="YYYY-MM-DD"
          start-placeholder="起"
          end-placeholder="止"
          class="!w-280px"
        />
      </el-form-item>
      <el-form-item label="供应商ID">
        <el-input v-model="queryParams.supplierId" class="!w-140px" clearable placeholder="可选" />
      </el-form-item>
      <el-form-item label="订单号">
        <el-input v-model="queryParams.orderNo" class="!w-180px" clearable />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </el-form-item>
    </el-form>

    <el-row :gutter="16" class="mb-16px">
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">可出账行 / 总行</div>
          <div class="stat-value">{{ summary.billableLineCount ?? 0 }} / {{ summary.lineCount ?? 0 }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">成交实付合计</div>
          <div class="stat-value">¥{{ fenToYuan(summary.payPriceSum) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">应付供（可出账）</div>
          <div class="stat-value">¥{{ fenToYuan(summary.payableSum) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">毛利差预估</div>
          <div class="stat-value">¥{{ fenToYuan(summary.marginSum) }}</div>
        </div>
      </el-col>
    </el-row>

    <el-divider content-position="left">本店 × 供 汇总（共 {{ groupTotal }} 组）</el-divider>
    <el-table v-loading="groupLoading" :data="groups" stripe>
      <el-table-column label="供应商" min-width="140">
        <template #default="{ row }">{{ row.supplierName || row.supplierId }}</template>
      </el-table-column>
      <el-table-column label="可出账/总行" width="100">
        <template #default="{ row }">{{ row.billableLineCount ?? 0 }} / {{ row.lineCount ?? 0 }}</template>
      </el-table-column>
      <el-table-column label="成交实付" width="110">
        <template #default="{ row }">{{ fenToYuan(row.payPriceSum) }}</template>
      </el-table-column>
      <el-table-column label="应付供预估" width="110">
        <template #default="{ row }">{{ fenToYuan(row.payableSum) }}</template>
      </el-table-column>
      <el-table-column label="毛利差" width="110">
        <template #default="{ row }">{{ fenToYuan(row.marginSum) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="90">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDrawer(row)">明细</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.groupPageSize"
      v-model:page="queryParams.groupPageNo"
      :total="groupTotal"
      @pagination="loadSummary"
    />

    <el-drawer v-model="drawerVisible" :title="drawerTitle" size="720px" destroy-on-close>
      <div v-if="drawerMeta" class="drawer-meta mb-12px">
        可出账应付 ¥{{ fenToYuan(drawerMeta.payableSum) }}
      </div>
      <el-table v-loading="drawerLoading" :data="drawerList" stripe size="small">
        <el-table-column label="订单号" prop="orderNo" min-width="150" />
        <el-table-column label="商品" prop="spuName" min-width="140" />
        <el-table-column label="成交实付" width="90">
          <template #default="{ row }">{{ fenToYuan(row.payPrice) }}</template>
        </el-table-column>
        <el-table-column label="结算单价" width="90">
          <template #default="{ row }">
            {{ row.settleUnitPrice != null ? fenToYuan(row.settleUnitPrice) : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="价来源" prop="settlePriceSourceName" min-width="120" />
        <el-table-column label="应付供" width="90">
          <template #default="{ row }">
            {{ row.payableAmount != null ? fenToYuan(row.payableAmount) : '—' }}
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="drawerQuery.pageSize"
        v-model:page="drawerQuery.pageNo"
        :total="drawerTotal"
        @pagination="loadDrawerLines"
      />
    </el-drawer>
  </ContentWrap>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import {
  getMerchantSettlePreviewPage,
  getMerchantSettlePreviewSummary
} from '@/api/mall/trade/supplierSettlePreview'

defineOptions({ name: 'MerchantSupplierSettlePreview' })

const fenToYuan = (fen?: number | null) => ((fen ?? 0) / 100).toFixed(2)

const defaultFinishRange = (): string[] => [
  dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD')
]

const queryParams = reactive({
  groupPageNo: 1,
  groupPageSize: 20,
  supplierId: undefined as string | undefined,
  orderNo: undefined as string | undefined,
  finishTime: defaultFinishRange() as string[] | undefined
})

const summary = ref<any>({})
const groups = ref<any[]>([])
const groupTotal = ref(0)
const groupLoading = ref(false)

const drawerVisible = ref(false)
const drawerLoading = ref(false)
const drawerList = ref<any[]>([])
const drawerTotal = ref(0)
const drawerTitle = ref('订单行明细')
const drawerMeta = ref<any>(null)
const drawerQuery = reactive({
  pageNo: 1,
  pageSize: 20,
  supplierId: undefined as number | undefined
})

const finishTimeParam = () => {
  if (!queryParams.finishTime || queryParams.finishTime.length !== 2) return null
  return [
    `${queryParams.finishTime[0]} 00:00:00`,
    `${queryParams.finishTime[1]} 23:59:59`
  ]
}

const summaryParams = () => {
  const finishTime = finishTimeParam()
  if (!finishTime) {
    ElMessage.warning('请选择完成时间（默认近 30 天）')
    return null
  }
  const p: any = {
    groupPageNo: queryParams.groupPageNo,
    groupPageSize: queryParams.groupPageSize,
    orderNo: queryParams.orderNo,
    finishTime
  }
  if (queryParams.supplierId) p.supplierId = Number(queryParams.supplierId)
  return p
}

const loadSummary = async () => {
  const p = summaryParams()
  if (!p) {
    groups.value = []
    groupTotal.value = 0
    summary.value = {}
    return
  }
  groupLoading.value = true
  try {
    const data = (await getMerchantSettlePreviewSummary(p)) || {}
    summary.value = data
    groups.value = data.groups || []
    groupTotal.value = data.groupTotal ?? (data.groups || []).length
  } finally {
    groupLoading.value = false
  }
}

const handleQuery = () => {
  queryParams.groupPageNo = 1
  loadSummary()
}

const resetQuery = () => {
  queryParams.supplierId = undefined
  queryParams.orderNo = undefined
  queryParams.finishTime = defaultFinishRange()
  handleQuery()
}

const loadDrawerLines = async () => {
  const finishTime = finishTimeParam()
  if (!finishTime) return
  drawerLoading.value = true
  try {
    const data = await getMerchantSettlePreviewPage({
      pageNo: drawerQuery.pageNo,
      pageSize: drawerQuery.pageSize,
      supplierId: drawerQuery.supplierId,
      orderNo: queryParams.orderNo,
      finishTime
    })
    drawerList.value = data?.list || []
    drawerTotal.value = data?.total || 0
  } finally {
    drawerLoading.value = false
  }
}

const openDrawer = async (row: any) => {
  drawerTitle.value = `订单行明细 · ${row.supplierName || row.supplierId}`
  drawerMeta.value = row
  drawerQuery.pageNo = 1
  drawerQuery.supplierId = row.supplierId
  drawerVisible.value = true
  await loadDrawerLines()
}

onMounted(handleQuery)
</script>

<style scoped>
.stat-card {
  padding: 12px 16px;
  background: var(--el-fill-color-light);
  border-radius: 6px;
}
.stat-label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.stat-value {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 600;
}
.drawer-meta {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
</style>
